import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, BookOpen, Mail, ArrowRight } from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// Floating support widget for the marketing site. Lightweight — no Intercom,
// no bundled live-chat SDK, no extra third-party network calls. Just a small
// popover with quick paths to help center, email, and WhatsApp.
//
// Hidden inside the dashboard (when path starts with /dashboard, /leads, etc.)
// so it doesn't block in-app UI. Hidden on /contact too — they're already
// in the contact form.
// ────────────────────────────────────────────────────────────────────────────

const HIDDEN_PATH_PREFIXES = [
  '/dashboard', '/leads', '/users', '/settings', '/roles', '/admin',
  '/tasks', '/reminders', '/lead-assign', '/teams', '/time-availability',
  '/campaigns', '/reports', '/billing', '/email-templates', '/whatsapp',
  '/profile', '/audit-log', '/developer', '/super-admin', '/onboarding',
  '/choose-plan', '/payment', '/contact', '/support',
];

const SUPPORT_EMAIL   = 'support@brixlead.com';

export function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Hide on app routes — only show on marketing site.
  const hidden = HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Close popover on Escape for accessibility.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  if (hidden) return null;

  return (
    <div className="fixed bottom-5 right-5 z-40">
      {open && (
        <div
          className="absolute bottom-16 right-0 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-2"
          role="dialog"
        >
          {/* Header */}
          <div className="bg-[#6366F1] text-white px-4 py-3 flex items-start justify-between">
            <div>
              <p className="font-semibold text-sm">How can we help?</p>
              <p className="text-xs text-indigo-200">Replies in &lt; 1 business day</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-indigo-200 hover:text-white"
              aria-label="Close support widget"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-3 space-y-1">
            <SupportLink
              icon={BookOpen}
              title="Browse Help Center"
              subtitle="Setup guides, FAQs"
              to="/help"
              onSelect={() => setOpen(false)}
            />
            <SupportLink
              icon={Mail}
              title="Email support"
              subtitle={SUPPORT_EMAIL}
              external
              href={`mailto:${SUPPORT_EMAIL}?subject=BrixLead%20support%20request`}
            />
          </div>

          <div className="border-t border-slate-100 px-3 py-2.5 bg-slate-50 text-center">
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="text-xs text-[#6366F1] font-semibold hover:underline inline-flex items-center gap-1"
            >
              Talk to sales <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="h-12 w-12 rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-xl flex items-center justify-center transition-colors"
        aria-label={open ? 'Close support widget' : 'Open support widget'}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>
    </div>
  );
}

function SupportLink({
  icon: Icon,
  title,
  subtitle,
  to,
  href,
  external,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  to?: string;
  href?: string;
  external?: boolean;
  onSelect?: () => void;
}) {
  const inner = (
    <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
      <div className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-[#6366F1]" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#1E293B]">{title}</p>
        <p className="text-xs text-[#64748B] truncate">{subtitle}</p>
      </div>
    </div>
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return (
    <Link to={to!} onClick={onSelect} className="block">
      {inner}
    </Link>
  );
}

// Minimal WhatsApp glyph — keeps the bundle smaller than a 100-icon library
// import for a single page.
function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.339 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.601.955-1 3.648 3.738-.961.64.379zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    </svg>
  );
}
